import { getAllowedPlannerTransports } from 'js/config/planner'

export const DEFAULT_SELECTED_TRANSPORTS = {
  BUS: true,
  SUBWAY: true,
  RAIL: true,
  BICYCLE_RENT: true,
  TAXI: true,
  BICYCLE: true,
  CAR: true,
  MOTORCYCLE: true,
  WALK: true
}

export function getInitialSelectedTransports(appCode) {
  const allowed = getAllowedPlannerTransports(appCode)

  if (!allowed) {
    return { ...DEFAULT_SELECTED_TRANSPORTS }
  }

  return Object.keys(DEFAULT_SELECTED_TRANSPORTS).reduce((acc, transport) => {
    acc[transport] = allowed.includes(transport)
    return acc
  }, {})
}

export function areAllAllowedTransportsSelected(appCode, selectedTransports) {
  const allowed = getAllowedPlannerTransports(appCode)
  const transportsToCheck = allowed || Object.keys(selectedTransports)

  return transportsToCheck.every(transport => selectedTransports[transport])
}

export function buildPlannerTransportOptions(transportOptions, appCode, translate) {
  const allowed = getAllowedPlannerTransports(appCode)

  const options = transportOptions.map(opt => ({
    ...opt,
    label: opt.labelKey ? translate(opt.labelKey) : opt.label
  }))

  return allowed
    ? options.filter(opt => allowed.includes(opt.value))
    : options
}

export function generatePlannerModeCalls(
  selectedPublicTransport,
  isBicycleRentSelected,
  selectedPrivateTransport,
  selectedPrivateBicycle
) {
  const calls = []

  if (selectedPublicTransport.length > 0) {
    calls.push([...selectedPublicTransport])
  }

  if (selectedPublicTransport.length > 0 && isBicycleRentSelected) {
    calls.push([...selectedPublicTransport, 'BICYCLE_RENT'])
  }

  if (isBicycleRentSelected) {
    calls.push(['BICYCLE_RENT'])
  }

  if (selectedPrivateTransport.length > 0) {
    calls.push(['CAR'])
  }

  if (selectedPrivateBicycle.length > 0) {
    calls.push(selectedPrivateBicycle)
  }

  calls.push(['WALK'])

  return calls
}

export function buildTransportModeCalls(selectedTransports, transportCategories) {
  const publicTransport = transportCategories.publicTransport
  const privateTransport = transportCategories.privateTransport
  const privateBicycle = transportCategories.privateBicycle
  const isBicycleRentSelected = selectedTransports.BICYCLE_RENT

  const selectedPublicTransport = publicTransport.filter(mode => selectedTransports[mode] && mode !== 'BICYCLE_RENT')
  const selectedPrivateTransport = privateTransport.filter(mode => selectedTransports[mode])
  const selectedPrivateBicycle = privateBicycle.filter(mode => selectedTransports[mode])

  return generatePlannerModeCalls(
    selectedPublicTransport,
    isBicycleRentSelected,
    selectedPrivateTransport,
    selectedPrivateBicycle
  )
}

export function resetPlannerOperators(selectedTransports, selectedOperators, transportOperatorMap) {
  const relevantTransports = Object.keys(transportOperatorMap).filter(transport =>
    selectedTransports[transport]
  )

  return Object.keys(selectedOperators).reduce((acc, operatorKey) => {
    acc[operatorKey] = relevantTransports.some(transport =>
      transportOperatorMap[transport].includes(operatorKey)
    )
    return acc
  }, {})
}

export function initializeSelectedPlannerOperators(operators, savedOperators = {}) {
  const selectedOperators = {}

  ;(operators || []).forEach(operator => {
    const name = operator.business_name
    selectedOperators[name] = savedOperators && savedOperators[name] !== undefined
      ? savedOperators[name]
      : true
  })

  return selectedOperators
}

export function buildPlannerOperatorOptions(masterOperatorsList, selectedOperators) {
  return masterOperatorsList.map(operator => {
    const uniqueKey = operator.agency_id || operator.id || operator.business_name
    const operatorName = operator.business_name

    const isSelected = selectedOperators[operatorName] !== undefined
      ? selectedOperators[operatorName]
      : true

    return {
      id: uniqueKey,
      value: operatorName,
      name: operatorName,
      icon: operator.logo_url || (operator.logo_url && operator.logo_url.url) || 'default-icon',
      selected: isSelected
    }
  })
}

export function buildPlannerMasterOperatorsUpdate(
  masterOperatorsList,
  newOperators,
  selectedOperators,
  savedOperators = {}
) {
  if (!Array.isArray(newOperators)) {
    return null
  }

  const currentKeys = masterOperatorsList.map(op => op.agency_id || op.id || op.business_name)
  const updatedList = [...masterOperatorsList]
  const selectedOperatorUpdates = {}

  newOperators.filter(Boolean).forEach(newOp => {
    const newKey = newOp.agency_id || newOp.id || newOp.business_name
    const operatorName = newOp.business_name

    if (!currentKeys.includes(newKey)) {
      updatedList.push(newOp)
    }

    if (operatorName && selectedOperators[operatorName] === undefined) {
      selectedOperatorUpdates[operatorName] = savedOperators[operatorName] !== undefined
        ? savedOperators[operatorName]
        : true
    }
  })

  return {
    masterOperatorsList: updatedList,
    selectedOperatorUpdates
  }
}
